import { CONFIG } from './config';

const STORAGE_KEY = 'apps-hub-auth';
const PKCE_KEY = 'apps-hub-pkce-verifier';

interface Tokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  expiresAt: number; // epoch ms
}

// ---- PKCE helpers ----

function base64url(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function sha256(text: string): Promise<Uint8Array> {
  const buf = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(text),
  );
  return new Uint8Array(buf);
}

function randomVerifier(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return base64url(bytes);
}

// ---- Storage ----

function readTokens(): Tokens | null {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as Tokens;
  } catch {
    return null;
  }
}

function writeTokens(t: Tokens): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(t));
}

function clearTokens(): void {
  localStorage.removeItem(STORAGE_KEY);
}

// ---- Public API ----

export function isAuthenticated(): boolean {
  const t = readTokens();
  return !!t && t.expiresAt > Date.now() + 30_000;
}

export async function startLogin(): Promise<void> {
  const verifier = randomVerifier();
  const challengeBytes = await sha256(verifier);
  const challenge = base64url(challengeBytes);
  sessionStorage.setItem(PKCE_KEY, verifier);
  const url = new URL(`${CONFIG.cognitoDomain}/oauth2/authorize`);
  url.searchParams.set('client_id', CONFIG.clientId);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('scope', 'openid email profile');
  url.searchParams.set('redirect_uri', CONFIG.redirectUri);
  url.searchParams.set('code_challenge', challenge);
  url.searchParams.set('code_challenge_method', 'S256');
  window.location.assign(url.toString());
}

export async function exchangeCode(code: string): Promise<void> {
  const verifier = sessionStorage.getItem(PKCE_KEY);
  if (!verifier) throw new Error('missing PKCE verifier');
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: CONFIG.clientId,
    code,
    redirect_uri: CONFIG.redirectUri,
    code_verifier: verifier,
  });
  const res = await fetch(`${CONFIG.cognitoDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  if (!res.ok) throw new Error(`token exchange failed: ${res.status}`);
  const data = await res.json();
  writeTokens({
    idToken: data.id_token,
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
    expiresAt: Date.now() + (data.expires_in - 60) * 1000,
  });
  sessionStorage.removeItem(PKCE_KEY);
}

async function refresh(): Promise<Tokens | null> {
  const t = readTokens();
  if (!t?.refreshToken) return null;
  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: CONFIG.clientId,
    refresh_token: t.refreshToken,
  });
  const res = await fetch(`${CONFIG.cognitoDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  if (!res.ok) {
    clearTokens();
    return null;
  }
  const data = await res.json();
  const fresh: Tokens = {
    idToken: data.id_token,
    accessToken: data.access_token,
    refreshToken: data.refresh_token || t.refreshToken,
    expiresAt: Date.now() + (data.expires_in - 60) * 1000,
  };
  writeTokens(fresh);
  return fresh;
}

export async function getIdToken(): Promise<string | null> {
  let t = readTokens();
  if (!t) return null;
  if (t.expiresAt <= Date.now() + 30_000) {
    t = await refresh();
  }
  return t?.idToken ?? null;
}

export function logout(): void {
  clearTokens();
  const url = new URL(`${CONFIG.cognitoDomain}/logout`);
  url.searchParams.set('client_id', CONFIG.clientId);
  url.searchParams.set('logout_uri', CONFIG.logoutRedirect);
  window.location.assign(url.toString());
}
